import React, { useEffect, useState, useCallback } from "react";
import api, { API, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";

export function FieldInput({ field, value, onChange, testid }) {
  if (field.type === "select") {
    const opts = typeof field.options === "function" ? field.options() : field.options || [];
    return (
      <Select value={value ? String(value) : ""} onValueChange={(v) => onChange(field.name, v)}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={field.placeholder || "Seçiniz"} /></SelectTrigger>
        <SelectContent>
          {opts.map((o) => (
            <SelectItem key={o.value} value={String(o.value)} data-testid={`${testid}-opt-${o.value}`}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "number")
    return <Input type="number" step="any" data-testid={testid} value={value ?? ""}
      onChange={(e) => onChange(field.name, e.target.value === "" ? "" : Number(e.target.value))} />;
  if (field.type === "date")
    return <Input type="date" data-testid={testid} value={value ?? ""} onChange={(e) => onChange(field.name, e.target.value)} />;
  if (field.type === "checkbox")
    return <input type="checkbox" data-testid={testid} checked={!!value}
      onChange={(e) => onChange(field.name, e.target.checked)} className="w-4 h-4 accent-red-600" />;
  if (field.type === "image")
    return <ImageField value={value} onChange={(v) => onChange(field.name, v)} testid={testid} />;
  return <Input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(field.name, e.target.value)} />;
}

export function ImageField({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd);
      onChange(r.data.path);
      toast.success("Resim yüklendi");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <Input type="file" accept="image/*" onChange={upload} data-testid={testid} className="text-xs" />
      {uploading && <span className="text-xs text-muted-foreground">Yükleniyor...</span>}
      {value && !uploading && (
        <img src={`${API}/files/${value}`} alt="resim" data-testid={`${testid}-preview`}
          className="w-20 h-20 object-cover rounded border border-border" />
      )}
    </div>
  );
}

export default function CrudPage({ title, subtitle, endpoint, columns, fields, testidPrefix, defaultForm = {}, canDelete, toPayload, toForm }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/${endpoint}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const set = (name, v) => setForm((f) => ({ ...f, [name]: v }));

  const openNew = () => { setEditing(null); setForm({ ...defaultForm }); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm(toForm ? toForm(item) : { ...defaultForm, ...item }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = toPayload ? toPayload(form) : form;
      if (editing) await api.put(`/${endpoint}/${encodeURIComponent(editing.code)}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(editing ? "Kayıt güncellendi" : "Kayıt eklendi");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`"${item.name || item.code}" silinsin mi?`)) return;
    try { await api.delete(`/${endpoint}/${encodeURIComponent(item.code)}`); toast.success("Kayıt silindi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={`${testidPrefix}-page`}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <Button onClick={openNew} data-testid={`${testidPrefix}-add-button`}>
          <Plus size={16} className="mr-1.5" /> Yeni Ekle
        </Button>
      </div>
      <Card className="bg-card border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              {columns.map((c) => <TableHead key={c.key} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</TableHead>)}
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.code || item.name} className="border-border" data-testid={`${testidPrefix}-row-${item.code || item.name}`}>
                {columns.map((c) => (
                  <TableCell key={c.key} className="text-sm py-2.5">
                    {c.render ? c.render(item) : (item[c.key] ?? "-")}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`${testidPrefix}-edit-${item.code || item.name}`} onClick={() => openEdit(item)}>
                    <Pencil size={14} />
                  </Button>
                  {canDelete !== false && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid={`${testidPrefix}-delete-${item.code || item.name}`} onClick={() => remove(item)}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-muted-foreground py-8" data-testid={`${testidPrefix}-empty`}>Kayıt bulunamadı</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <CrudFormDialog open={open} setOpen={setOpen} editing={editing} fields={fields}
        form={form} setField={set} save={save} saving={saving} testidPrefix={testidPrefix} />
    </div>
  );
}

function CrudFormDialog({ open, setOpen, editing, fields, form, setField, save, saving, testidPrefix }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{editing ? "Kaydı Düzenle" : "Yeni Kayıt"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {fields.map((f) => (
            <div key={f.name} className={`space-y-1.5 ${f.full ? "sm:col-span-2" : ""}`}>
              <Label>{f.label}{f.required && " *"}</Label>
              <FieldInput field={f} value={form[f.name]} onChange={setField}
                testid={`${testidPrefix}-field-${f.name}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testidPrefix}-cancel`}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid={`${testidPrefix}-save`}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ActiveBadge({ value }) {
  return value === false
    ? <Badge variant="outline" className="text-muted-foreground border-muted">Pasif</Badge>
    : <Badge className="bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/20 border-0">Aktif</Badge>;
}
